import React, { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { QCODES } from "../data/qcodes";
import { Radio, Search, Printer, List, Layers, RotateCcw } from "lucide-react";

function QFlashCard({ c }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      className="flip-card h-44 cursor-pointer"
      onClick={() => setFlipped((f) => !f)}
      data-testid={`qcode-card-${c.code}`}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={c.code + (flipped ? "-b" : "-f")}
          initial={{ rotateY: flipped ? -90 : 90, opacity: 0 }}
          animate={{ rotateY: 0, opacity: 1 }}
          exit={{ rotateY: flipped ? 90 : -90, opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="flip-inner w-full h-full"
        >
          <div className="w-full h-full rounded-sm border border-border bg-card p-4 flex flex-col">
            {!flipped ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center gap-2">
                <span className="font-mono font-black text-4xl text-primary tracking-tight">{c.code}</span>
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                  Cliquez pour révéler
                </span>
              </div>
            ) : (
              <div className="flex-1 flex flex-col justify-center gap-3 text-left">
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-primary mb-0.5">Affirmation</div>
                  <div className="text-sm leading-snug">{c.statement}</div>
                </div>
                <div>
                  <div className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">Question</div>
                  <div className="text-sm text-muted-foreground leading-snug">{c.question}</div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-border">
              <span className="font-mono text-xs font-bold text-primary">{c.code}</span>
              <RotateCcw size={13} className="text-muted-foreground" />
            </div>
          </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

export default function QCodes() {
  const [query, setQuery] = useState("");
  const [view, setView] = useState("list"); // list | cards
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return QCODES.filter(
      (c) =>
        !q ||
        c.code.toLowerCase().includes(q) ||
        c.statement.toLowerCase().includes(q) ||
        c.question.toLowerCase().includes(q)
    );
  }, [query]);

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-primary">
            <Radio size={14} /> Références
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tight mt-2">Codes Q</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-xl">
            Abréviations internationales à trois lettres. Chaque code a un sens en <span className="text-foreground font-medium">affirmation</span> et en <span className="text-foreground font-medium">question</span>.
          </p>
        </div>
        <button onClick={() => window.print()} data-testid="qcode-print" className="print:hidden inline-flex items-center gap-2 rounded-sm border border-border px-4 py-2.5 text-sm font-semibold hover:bg-secondary transition-colors shrink-0">
          <Printer size={16} /> Imprimer
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative max-w-md flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            data-testid="qcode-search"
            placeholder="Rechercher un code (ex. QSL, position…)"
            className="w-full rounded-sm border border-border bg-card pl-9 pr-3 py-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        </div>
        <div className="inline-flex rounded-sm border border-border p-0.5 shrink-0 print:hidden">
          <button
            onClick={() => setView("list")}
            data-testid="qcode-view-list"
            className={`inline-flex items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-semibold transition-colors ${view === "list" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            <List size={15} /> Liste
          </button>
          <button
            onClick={() => setView("cards")}
            data-testid="qcode-view-cards"
            className={`inline-flex items-center gap-1.5 rounded-sm px-3 py-2 text-sm font-semibold transition-colors ${view === "cards" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}
          >
            <Layers size={15} /> Cartes
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="text-center text-sm text-muted-foreground py-12 rounded-sm border border-border">Aucun code trouvé.</div>
      ) : view === "list" ? (
        <div className="rounded-sm border border-border overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 bg-secondary/60 text-xs font-mono uppercase tracking-wider text-muted-foreground px-4 py-2.5">
            <div className="col-span-2">Code</div>
            <div className="col-span-5">Affirmation</div>
            <div className="col-span-5">Question</div>
          </div>
          {filtered.map((c, i) => (
            <motion.div
              key={c.code}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: Math.min(i * 0.015, 0.25) }}
              data-testid={`qcode-${c.code}`}
              className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-0 px-4 py-3 border-t border-border hover:bg-secondary/30 transition-colors"
            >
              <div className="col-span-2 font-mono font-bold text-primary">{c.code}</div>
              <div className="col-span-5 text-sm sm:pr-4">
                <span className="sm:hidden font-mono text-[10px] uppercase text-muted-foreground mr-1">Aff.</span>
                {c.statement}
              </div>
              <div className="col-span-5 text-sm text-muted-foreground">
                <span className="sm:hidden font-mono text-[10px] uppercase mr-1">Quest.</span>
                {c.question}
              </div>
            </motion.div>
          ))}
        </div>
      ) : (
        <div data-testid="qcode-cards-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {filtered.map((c) => (
            <QFlashCard key={c.code} c={c} />
          ))}
        </div>
      )}
    </div>
  );
}
